import { DateTime } from 'luxon'

import { validateEndDate, validateStartDate } from '@functions/dateValidation'

const startOfMonth = DateTime.now().startOf('month')
const startOfLastMonth = startOfMonth.minus({ months: 1 })

const endDate: string = startOfMonth.toISODate()
const startDate: string = startOfLastMonth.toISODate()

export type ReportKeyType =
'agency_cmf_and_subscriptions'
  | 'activity_statement'
  | 'advanced_ordered_item_summary'
  | 'advanced_pending_ordered_item_summary'
  | 'agencies_turnover'
  | 'agency_revenue_leakage'
  | 'agency_subscriptions'
  | 'approved_order_summary'
  | 'approved_orders'
  | 'artwork_status'
  | 'brand_invoices_total'
  | 'campaign_quote_template_product_usage_report'
  | 'campaign_summary'
  | 'campaigns_with_approved_orders'
  | 'cost_of_sale'
  | 'digital_vs_print'
  | 'installed_signboards'
  | 'media_publications_billing'
  | 'no_approved_orders'
  | 'off_market_sales'
  | 'ordered_item_summary'
  | 'order_reconciliation_by_agency'
  | 'order_reconciliation_by_campaign'
  | 'order_reconciliation_by_provider'
  | 'order_totals_provider'
  | 'order_totals_publication'
  | 'ordered_item_overview'
  | 'ordered_product_quantities'
  | 'payments_not_received'
  | 'portal_remapping'
  | 'price_list_details'
  | 'price_list_utilisation_report'
  | 'processing_fees'
  | 'proposals_created_per_month'
  | 'provider_packages'
  | 'publication_edition_orders'
  | 'publication_order_item_summary'
  | 'provider_revenue_leakage'
  | 'sales_commission_report'
  | 'template_page_usage_advanced'
  | 'template_page_usage'
  | 'template_rejections'
  | 'tracking_links_activity'
  | 'training_lesson_views'
  | 'unused_templates'
  | 'user_details'

export type ReportDefaultStateType = {
  agency_id?: number,
  brand_id?: number,
  campaign_id?: number,
  campaign_status?: string,
  creditor_type?: string,
  date_column?: string,
  email?: string,
  end_date?: string,
  provider_id?: number,
  provider_price_list_id?: number,
  publication_edition_id?: number,
  publication_id?: number,
  publication_order_id?: number,
  role_id?: number,
  start_date?: string,
  string?: string,
  user_id?: number,
}

type ReportModels = {
  [Key in ReportKeyType as string]: {
    defaultState: ReportDefaultStateType,
    requiredFields: {
      key: string,
      validation?: Function,
      invalidMessage?: string,
    }[],
  }
}

const reportModels: ReportModels = {
  agency_cmf_and_subscriptions: {
    defaultState: { email: '' },
    requiredFields: [{ key: 'email' }],
  },
  activity_statement: {
    defaultState: { campaign_id: undefined },
    requiredFields: [{ key: 'campaign_id' }],
  },
  advanced_ordered_item_summary: {
    defaultState: {
      brand_id: undefined,
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  advanced_pending_ordered_item_summary: {
    defaultState: {
      brand_id: undefined,
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  agencies_turnover: {
    defaultState: {
      email: '',
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  agency_revenue_leakage: {
    defaultState: {
      end_date: endDate,
      agency_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      {
        key: 'end_date',
        validation:
        // eslint-disable-next-line max-len
        (selectedEndDate: string, state: ReportDefaultStateType) => validateEndDate({ endDate: selectedEndDate, startDate: state.start_date }),
        invalidMessage: 'Max 92 days',
      },
      {
        key: 'start_date',
        validation:
        // eslint-disable-next-line max-len
        (selectedStartDate: string, state: ReportDefaultStateType) => validateStartDate({ endDate: state.end_date, startDate: selectedStartDate }),
      },
    ],
  },
  agency_subscriptions: {
    defaultState: {},
    requiredFields: [],
  },
  approved_orders: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'provider_id' },
      { key: 'start_date' },
    ],
  },
  approved_order_summary: {
    defaultState: {
      email: '',
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  artwork_status: {
    defaultState: { publication_order_id: undefined },
    requiredFields: [{ key: 'publication_order_id' }],
  },
  brand_invoices_total: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  campaign_quote_template_product_usage_report: {
    defaultState: {
      brand_id: undefined,
      provider_id: undefined,
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'provider_id' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  campaign_summary: {
    defaultState: {
      email: '',
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  campaigns_with_approved_orders: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  cost_of_sale: {
    defaultState: { campaign_id: undefined },
    requiredFields: [{ key: 'campaign_id' }],
  },
  digital_vs_print: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  installed_signboards: {
    defaultState: {},
    requiredFields: [],
  },
  monthly_proposals_created: {
    defaultState: {},
    requiredFields: [],
  },
  media_publications_billing: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'provider_id' },
      { key: 'start_date' },
    ],
  },
  no_approved_orders: {
    defaultState: {},
    requiredFields: [],
  },
  off_market_sales: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  ordered_item_overview: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'provider_id' },
      { key: 'start_date' },
    ],
  },
  ordered_item_summary: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  order_reconciliation_by_agency: {
    defaultState: {
      end_date: endDate,
      date_column: 'approval_date',
      campaign_status: 'all',
      start_date: startDate,
    },
    requiredFields: [
      { key: 'campaign_status' },
      { key: 'date_column' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  order_reconciliation_by_campaign: {
    defaultState: {
      end_date: endDate,
      date_column: 'approval_date',
      campaign_status: 'all',
      start_date: startDate,
    },
    requiredFields: [
      { key: 'campaign_status' },
      { key: 'date_column' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  order_reconciliation_by_provider: {
    defaultState: {
      agency_id: undefined,
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'provider_id' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  ordered_product_quantities: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  order_totals_provider: {
    defaultState: {
      end_date: endDate,
      date_column: 'approval_date',
      start_date: startDate,
    },
    requiredFields: [
      { key: 'date_column' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  order_totals_publication: {
    defaultState: {
      end_date: endDate,
      date_column: 'approval_date',
      start_date: startDate,
    },
    requiredFields: [
      { key: 'date_column' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  portal_remapping: {
    defaultState: {
      agency_id: undefined,
    },
    requiredFields: [
      { key: 'agency_id' },
    ],
  },
  price_list_details: {
    defaultState: {
      provider_price_list_id: undefined,
    },
    requiredFields: [
      { key: 'provider_price_list_id' },
    ],
  },
  price_list_utilisation_report: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
      provider_id: undefined,
    },
    requiredFields: [
      { key: 'provider_id' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  processing_fees: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'provider_id' },
      { key: 'start_date' },
    ],
  },
  provider_packages: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'provider_id' },
      { key: 'start_date' },
    ],
  },
  payments_not_received: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  publication_edition_orders: {
    defaultState: {
      publication_edition_id: undefined,
    },
    requiredFields: [],
  },
  publication_order_item_summary: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      publication_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'provider_id' },
      { key: 'publication_id' },
    ],
  },
  provider_revenue_leakage: {
    defaultState: {
      end_date: endDate,
      provider_id: undefined,
      start_date: startDate,
    },
    requiredFields: [
      {
        key: 'end_date',
        validation:
        // eslint-disable-next-line max-len
        (selectedEndDate: string, state: ReportDefaultStateType) => validateEndDate({ endDate: selectedEndDate, startDate: state.start_date }),
        invalidMessage: 'Max 92 days',
      },
      {
        key: 'start_date',
        validation:
        // eslint-disable-next-line max-len
        (selectedStartDate: string, state: ReportDefaultStateType) => validateStartDate({ endDate: state.end_date, startDate: selectedStartDate }),
      },
    ],
  },
  sales_commission_report: {
    defaultState: {
      end_date: endDate,
      role_id: undefined,
      start_date: startDate,
      user_id: undefined,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'user_id' },
      { key: 'start_date' },
    ],
  },
  template_page_usage: {
    defaultState: {
      agency_id: undefined,
      brand_id: undefined,
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'brand_id' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  template_page_usage_advanced: {
    defaultState: {
      agency_id: undefined,
      brand_id: undefined,
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'brand_id' },
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  template_rejections: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  tracking_links_activity: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  training_lesson_views: {
    defaultState: {},
    requiredFields: [],
  },
  unused_templates: {
    defaultState: {
      end_date: endDate,
      start_date: startDate,
    },
    requiredFields: [
      { key: 'end_date' },
      { key: 'start_date' },
    ],
  },
  user_details: {
    defaultState: {
      agency_id: undefined,
      brand_id: undefined,
      provider_id: undefined,
      role_id: undefined,
      string: '',
    },
    requiredFields: [],
  },
}

export default reportModels
